import CustomSelect from '../Shared/CustomSelect/CustomSelect';
import { useTranslation } from 'react-i18next';
import { Button, IconButton, InputBar } from '~/ui';
import ReactQuill, { Quill } from 'react-quill';

import styles from './CreateArticle.module.scss';
import 'react-quill/dist/quill.snow.css';
import './Editor.scss';
import { useFormik } from 'formik';
import { instance } from '~/utils/api/api';
import { useToastError } from '~/utils/useToastError';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToastSuccess } from '~/utils/useToastSuccess';
import { useEffect, useMemo, useRef, useState } from 'react';
import { validate } from '~/utils/validateArticle';
import CloseIcon from '~/assets/svg/newSvg/close-cross.svg?react';
import PhotoIcon from '~/assets/svg/knowledge/photo.svg?react';
import FileIcon from '~/assets/svg/newSvg/files.svg?raw';
import { Modal } from '../Shared/Modal/Modal';
import ImageArtickeContainer from './ImageArtickeContainer/ImageArtickeContainer';
import { useNavigate, useParams } from 'react-router-dom';
import ImageQuillContainer from './ImageQuillContainer/ImageQuillContainer';
import TextEditor from '~/ui/TextEditor/TextEditor';
import FilesQuillContainer from '~/ui/TextEditor/FilesQuillContainer/FilesQuillContainer';

const icons = Quill.import('ui/icons');
icons.file = FileIcon;

interface ICreateProps {
  title: string;
  category_id: number;
  content: string;
  image_ids: number[];
  main_photo_id: number | null;
}

interface IPhotos {
  full_photo_url: string;
  id: number;
  path: string;
}

interface IFormik {
  name: string;
  category: { id: number; name: string };
  content: string;
  mainPhoto: IPhotos;
  photos: IPhotos[] | [];
}

const CreateArticle = () => {
  const { t } = useTranslation();
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const toastError = useToastError();
  const toastSuccess = useToastSuccess();
  const quillRef = useRef<ReactQuill | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isQuilModalOpen, setIsQuilModalOpen] = useState(false);
  const [isFilesQuilModalOpen, setIsFilesQuilModalOpen] = useState(false);
  const [currentAsset, setCurrentAsset] = useState(0);

  const handleInsertImage = (imageUrl: string[]) => {
    const editor = quillRef.current?.getEditor();
    if (quillRef.current) {
      quillRef.current.focus();
    }
    if (editor) {
      const range = editor.getSelection();
      if (range) {
        const customImage = imageUrl
          .map(
            (item, idx) =>
              `<img
                  src="${item}"
                  alt="article image ${idx + 1}"
                />`
          )
          .join('');
        editor.clipboard.dangerouslyPasteHTML(range.index, customImage);
      }
    }
  };

  const handleInsertFile = (data: { full_path: string; name: string }[]) => {
    const editor = quillRef.current?.getEditor();
    if (quillRef.current) {
      quillRef.current.focus();
    }
    if (editor) {
      const range = editor.getSelection();
      if (range) {
        const fileLink = data
          .map(
            item =>
              `<a
                  href="${item.full_path}"
                  target="_blank"
                >${item.name}</a>&nbsp;`
          )
          .join(', ');
        editor.clipboard.dangerouslyPasteHTML(range.index, fileLink);
      }
    }
  };

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, false] }],
          ['bold', 'italic', 'underline', 'link'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ align: [] }],
          ['image'],
          ['file'],
        ],
        handlers: {
          image: () => setIsQuilModalOpen(true),
          file: () => setIsFilesQuilModalOpen(true),
        },
      },
    }),
    []
  );

  const client = useQueryClient();

  const { mutateAsync: createArticleMethod } = useMutation(
    async ({
      title,
      category_id,
      content,
      image_ids,
      main_photo_id,
    }: ICreateProps) => {
      const response = await instance.post(`admin/knowledge-base-articles`, {
        title,
        category_id,
        content,
        image_ids,
        main_photo_id,
      });
      return response.data;
    },
    {
      onSuccess: () => {
        client.invalidateQueries(['knowledge-articles']);
        toastSuccess(t('article_successfull_created'));
        navigate(`/knowledge-base/${categoryId}`);
      },
      onError: error => {
        toastError(error);
      },
    }
  );

  const { data: categoryModules, isLoading } = useQuery({
    queryFn: async () => {
      const response = await instance.get('admin/knowledge-base-categories');
      return response.data;
    },
    queryKey: ['base-modules'],
    onError: error => {
      toastError(error);
    },
  });

  const { data: currentCategory } = useQuery({
    queryFn: async () => {
      const response = await instance.get(
        `admin/knowledge-base-categories/${categoryId}`
      );
      return response.data;
    },
    queryKey: ['knowledge-base', categoryId],
    onError: error => {
      toastError(error);
    },
  });

  const getModuleOptions = (data: {
    [key: string]: {
      id: number;
      name: string;
      status: string;
    };
  }) => {
    if (!data) return [];
    const modulesOptions = Object.values(data);
    return modulesOptions;
  };

  const modulesOptions = getModuleOptions(categoryModules?.data);

  const formik = useFormik<IFormik>({
    initialValues: {
      name: '',
      category: { id: 0, name: '' },
      content: '',
      photos: [],
      mainPhoto: {
        full_photo_url: '',
        id: 0,
        path: '',
      },
    },
    validate,
    onSubmit: values => {
      const image_ids = values.photos.map(item => item.id);
      const data = {
        title: values.name,
        content: values.content,
        category_id: values.category.id,
        image_ids: image_ids,
        main_photo_id: values.mainPhoto.id || null,
      };
      createArticleMethod(data);
    },
  });

  useEffect(() => {
    if (!modulesOptions.length && !categoryId) return;
    const category = modulesOptions.find(
      item => item.id === Number(categoryId)
    );
    formik.setFieldValue('category', {
      id: category?.id,
      name: category?.name,
    });
  }, []);

  useEffect(() => {
    if (!currentCategory) return;
    setCurrentAsset(currentCategory.asset_id);
  }, [currentCategory]);

  return (
    <form onSubmit={formik.handleSubmit} className={styles.createArticle}>
      <div className={styles.inputLine}>
        <InputBar
          name="name"
          label={t('article_name')}
          placeholder={t('article_name_validate')}
          value={formik.values.name}
          onChange={formik.handleChange}
          star
          errors={formik.errors.name}
          onBlur={formik.handleBlur}
          error={formik.touched.name && Boolean(formik.errors.name)}
        />
        <CustomSelect
          label={t('select_category_validate')}
          className={styles.categorySelect}
          placeholder={t('to_choose')}
          value={formik.values.category}
          options={modulesOptions}
          disabled={!categoryModules && isLoading}
          onChange={value => formik.setFieldValue('category', value)}
          required
          error={
            formik.touched.category
              ? (formik.errors.category?.name as string)
              : undefined
          }
          onTouch={() => formik.setFieldTouched('category', true)}
        />
      </div>

      {formik.values.mainPhoto.id !== 0 ? (
        <div className={styles.imagesWrapper}>
          <p className={styles.imageTitle}>{t('cover')}</p>
          <div className={styles.imageItem}>
            <img
              src={formik.values.mainPhoto.full_photo_url}
              className={styles.image}
            />
            <button
              type="button"
              className={styles.deleteBtn}
              onClick={() =>
                formik.setFieldValue('mainPhoto', {
                  id: 0,
                  full_photo_url: '',
                  path: '',
                })
              }
            >
              <CloseIcon />
            </button>
          </div>
        </div>
      ) : (
        <div className={styles.addIconWrapper}>
          <IconButton
            onClick={() => setIsModalOpen(true)}
            className={styles.pickPhotoButton}
          >
            <PhotoIcon className={styles.icon} />
            <span className={styles.pickPhotoText}>
              {t('select_article_cover')}
            </span>
          </IconButton>
        </div>
      )}

      <div className={styles.quilWrapper}>
        <TextEditor
          ref={quillRef}
          className={styles.quill}
          value={formik.values.content}
          name={'content'}
          onChange={formik.setFieldValue}
          modules={modules}
        />
        {formik.touched.content && formik.errors.content && (
          <span className={styles.errorMsg}>{t('fill_field')}</span>
        )}
      </div>
      <Button
        text={t('save_n_finish')}
        className={styles.saveButton}
        type="submit"
        disabled={!!Object.keys(formik.errors).length}
      />

      <Modal
        modalStyles={styles.photosModal}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      >
        <ImageArtickeContainer
          selectedImage={formik.values.mainPhoto}
          onCloseModal={() => setIsModalOpen(false)}
          onSelectImages={values => formik.setFieldValue('mainPhoto', values)}
        />
      </Modal>
      <Modal
        modalStyles={styles.photosModal}
        isOpen={isQuilModalOpen}
        onClose={() => setIsQuilModalOpen(false)}
      >
        <ImageQuillContainer
          isOpen={isQuilModalOpen}
          onClose={() => setIsQuilModalOpen(false)}
          onInsertImage={handleInsertImage}
        />
      </Modal>
      <Modal
        modalStyles={styles.photosModal}
        isOpen={isFilesQuilModalOpen}
        onClose={() => setIsFilesQuilModalOpen(false)}
      >
        <FilesQuillContainer
          assetId={currentAsset}
          isOpen={isFilesQuilModalOpen}
          onClose={() => setIsFilesQuilModalOpen(false)}
          handleInsertFile={handleInsertFile}
        />
      </Modal>
    </form>
  );
};

export default CreateArticle;
